// Spec 078 — o ritmo como COMPARAÇÃO, não como dois números crus. Uma fonte (`ratePresentation`)
// e duas apresentações: `panel` (tabela, barra sobre a leitura) e `card` (cartão da máquina na
// cena, tudo numa linha porque o cartão flutua sobre o modelo e não pode crescer).
//
// A barra nunca carrega o sentido sozinha: a porcentagem em número anda sempre ao lado (a mesma
// regra de "status nunca só por cor"). Ausência não vira barra — vira o seu nome.
//
// Quem decide o que aparece é ESTE componente, a partir de uma única `ratePresentation`: o cartão
// omite a ausência que o próprio label de status já diz, e o link "cadastrar" nasce aqui em vez de
// vir pronto de fora (a condição morava em dois arquivos e podia divergir em silêncio).
import * as React from "react";
import { TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { Bar } from "@/components/ui/bar";
import { ratePresentation, formatPercent } from "../scene-rate";
import { useOeeThresholds, type OeeThresholds } from "@/hooks/useOrganizationPolicy";
import type { SceneMachine } from "@/types/scene.types";

export interface RateMeterProps {
  machine: SceneMachine;
  variant: "panel" | "card";
  /** rota ambiente/TV: fonte e barra maiores */
  far?: boolean;
  /** destino do atalho "cadastrar" ao lado de uma ausência acionável; sem ele, sem link */
  registerHref?: string;
  /**
   * A régua de cor, quando quem renderiza está do outro lado de uma fronteira de contexto.
   *
   * ⚠️ EXISTE POR CAUSA DO `<Html>` DO DREI. Ele monta os filhos num `ReactDOM.createRoot` PRÓPRIO,
   * sem ponte de contexto — então o cartão que flutua sobre a máquina na cena 3D não enxerga o
   * `OrganizationPolicyProvider`, e cairia nos padrões 85/65 enquanto o painel textual (DOM normal,
   * mesmo componente) usa a régua da fábrica. Mesma máquina, mesma tela, duas réguas — exatamente
   * a contradição que a Spec 079 existe para eliminar.
   *
   * O `<Canvas>` do fiber 8 BRIDGA contexto (via `its-fine`), então quem está dentro da cena e
   * fora do `<Html>` — o `MachineLabel` — consegue ler a régua e passá-la por aqui. É só essa
   * fronteira que precisa ser atravessada à mão.
   */
  thresholds?: OeeThresholds;
}

const BAR_WIDTH = {
  panel: { near: "w-24", far: "w-48" },
  card: { near: "w-12", far: "w-24" },
} as const;

export function RateMeter({
  machine,
  variant,
  far = false,
  registerHref,
  thresholds,
}: RateMeterProps): React.ReactElement | null {
  // Spec 079 — a régua de cor vem da organização; `scene-rate.ts` continua puro e sem query.
  // A prop VENCE o contexto: dentro do `<Html>` do drei o contexto não chega (ver `thresholds`).
  const fromContext = useOeeThresholds();
  const r = ratePresentation(machine, thresholds ?? fromContext);
  const panel = variant === "panel";

  if (r.kind === "absent") {
    // O cartão da cena não repete o que o dot + label de status já dizem: numa máquina OCIOSA a
    // repetição virava contradição ("· Ociosa" em cima, "parada" embaixo). O painel mantém a
    // célula preenchida — lá a coluna Ritmo não tem de onde mais tirar o estado.
    if (!panel && r.saidByStatus) return null;
    const warn = r.tone === "warn";
    return (
      <div
        className={`inline-flex items-center gap-1 ${warn ? "text-rd-warning-600" : "text-text-tertiary"} ${
          // O cartão flutua sobre a máquina: "sem papel de vazão declarado" + "cadastrar" numa
          // linha só passava da largura do nome (que já é `max-w-[16ch] truncate`) e cobria o
          // vizinho. No painel a frase fica inteira.
          panel ? "" : "max-w-[26ch]"
        }`}
      >
        {warn ? <TriangleAlert className={far ? "h-5 w-5" : "h-3 w-3"} aria-hidden /> : null}
        <span>{r.text}</span>
        {/* Sem leitura ainda existe um "deveria sair", e é ele que diz se a referência é um chute.
            O painel é a superfície completa — antes ele tinha uma coluna Nominal para isto. */}
        {panel ? (
          <span className="text-text-tertiary">
            · esperado {r.expected} ({r.provenance})
          </span>
        ) : null}
        {warn && !far && registerHref ? (
          <Link to={registerHref} className="pointer-events-auto underline">
            cadastrar
          </Link>
        ) : null}
      </div>
    );
  }

  const percent = formatPercent(r.percent);
  const label = `${r.comparison}, ${percent} do esperado`;
  const bar = (
    <Bar
      value={r.percent}
      tone={r.tone === "neutral" ? "brand" : r.tone}
      size={far ? "lg" : "sm"}
      ariaLabel={label}
      // A barra satura em 100 (Bar clampa) e `aria-valuenow` junto; sem isto um overrun de 200%
      // seria anunciado como "100 por cento", igualzinho a uma máquina no alvo.
      aria-valuetext={percent}
      // Leitura velha esmaece a BARRA (nunca o número — dimerizar texto quebra contraste). No
      // cartão, que não tem a frase "sem leitura desde", esta é a única marca — e é a que chega
      // na TV, onde o painel textual é inalcançável. O número ao lado segue firme e legível.
      data-stale={r.aged ? "true" : undefined}
      className={`shrink-0 ${BAR_WIDTH[variant][far ? "far" : "near"]} ${r.aged ? "opacity-50" : ""}`}
    />
  );

  const detail = `${r.comparison} (${r.provenance})`;

  // A marca de leitura velha vive SÓ no painel (decisão com o usuário): o cartão flutua sobre a
  // máquina e disputa espaço com ela. Custo aceito e sabido — no cartão, medida e envelhecida
  // ficam iguais, então quem olha só a TV não vê que o número parou no tempo.
  const staleness = panel && r.aged ? ` · sem leitura desde ${r.aged}` : "";

  // Cartão de perto: uma linha só — ele flutua sobre o modelo e disputa espaço com ele. Na TV
  // (`far`) a mesma linha ficava larga o bastante para cobrir o cartão vizinho, então empilha.
  if (!panel && !far) {
    return (
      <div className="inline-flex max-w-[28ch] items-center gap-1.5 overflow-hidden whitespace-nowrap text-text-secondary">
        {bar}
        <span className="shrink-0 font-medium tabular-nums">{percent}</span>
        <span className="min-w-0 truncate">· {detail}</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${panel ? "" : "items-center"}`}>
      <span className="flex items-center gap-2">
        {bar}
        <span className="font-medium tabular-nums text-text-primary">{percent}</span>
      </span>
      {/* truncate + max-w no cartão: um ritmo esperado cadastrado errado produz porcentagens de 5
          dígitos, e o cartão cresceria por cima do vizinho. No PAINEL a frase QUEBRA em vez de
          esticar: a tabela rola em `overflow-x-auto`, e na TV (`far`, text-2xl) uma célula que não
          quebra empurrava a coluna Calor para fora de uma barra de rolagem que ninguém alcança. */}
      <span className={panel ? "text-text-tertiary" : "max-w-[32ch] truncate text-text-tertiary"}>
        {detail}
        {staleness}
      </span>
    </div>
  );
}
